import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { motion } from 'motion/react';

import {
	Drawer,
	DrawerContent,
	DrawerTitle,
} from '@/components/ui/drawer';
import { isTextField } from '@/hooks/useDrawerKeyboardInset';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { playHaptic } from '@/shared/utils/haptics';
import { registerModal } from '@/shared/utils/nativeBridge';

// Un drawer modal (vaul/Radix) abierto debajo bloquea el wheel fuera de su
// propia capa: react-remove-scroll hace preventDefault a nivel documento y
// marca el lock con `data-scroll-locked` en el body. Cualquier contenedor
// scrolleable que viva FUERA de esa capa (el branch desktop de este Modal, o
// un área con scroll interno dentro de él) pierde el scroll nativo — mientras
// el lock esté activo scrolleamos imperativamente; sin lock no interferimos
// para no duplicar el scroll nativo.
export const wheelScrollUnderOverlayLock = (
	event: React.WheelEvent<HTMLElement>,
) => {
	const scrollLocked =
		document.body.hasAttribute('data-scroll-locked') ||
		document.body.style.pointerEvents === 'none';
	if (!scrollLocked) return;
	const delta = event.deltaMode === 1 ? event.deltaY * 32 : event.deltaY;
	event.currentTarget.scrollTop += delta;
};

const stripDrawerConflictingClasses = (className: string) =>
	className
		.split(/\s+/)
		.filter(
			(token) =>
				token &&
				!/^(?:(?:sm|md|lg|xl|2xl):)?!?(?:(?:min|max)-)?(?:[wh]|size)-/.test(token) &&
				!/^(?:(?:sm|md|lg|xl|2xl):)?!?overflow-(?:auto|scroll|x-|y-)/.test(token),
		)
		.join(' ');

type Props = {
	sx?: string;
	open: boolean;
	type?: 'dialog' | 'fullscreen';
	children: React.ReactNode;
	onClose?: () => void;
	showCloseButton?: boolean;
	fullScreenOnMobile?: boolean;
	/**
	 * Sigue abierto y MONTADO, pero otra capa lo tapa: se quita de la vista sin
	 * perder su estado. Es lo que necesita un modal anidado — cerrar el de abajo
	 * desmontaría su contenido (coreografías que se repiten, exposiciones de
	 * tracking que se cuentan dos veces), y dejarlo visible enseña dos paneles y
	 * dos X encimadas.
	 */
	hidden?: boolean;
	flush?: boolean;
	floatOnMobile?: boolean;
};

const Modal = ({
	open,
	children,
	onClose,
	sx = '',
	type = 'fullscreen',
	showCloseButton = true,
	fullScreenOnMobile = false,
	hidden = false,
	flush = false,
	floatOnMobile = false,
}: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const isMobile = useMediaQuery('(max-width: 767px)');

	// Taparse no es apagarse de golpe: el panel RETROCEDE —se encoge un poco y se
	// desvanece— mientras la capa de encima llega, y vuelve hacia adelante al
	// destaparse. Antes desaparecía de golpe y entre los dos quedaba un parpadeo
	// con la página desnuda.
	//
	// El delay sólo existe en mobile y sólo al ocultarse: el drawer de vaul tarda
	// 500ms en subir, así que el de abajo espera un poco antes de irse. No tiene
	// que cubrir los 500ms enteros, sólo evitar que retroceda antes de que el
	// otro se asome. En escritorio arranca de inmediato.
	// Al destaparse la clase desaparece entera —y el delay con ella— así que la
	// vuelta es inmediata, que es lo que se necesita: la capa de encima se
	// desmonta sin animación de salida.
	//
	// La transición vive en la base y no en la clase de tapado para que el
	// regreso también se anime. Al montar no dispara nada: los valores de
	// arranque ya son los de reposo.
	//
	// Va sobre `scale` (la propiedad, no `transform`) y sobre un contenedor que
	// no toca framer-motion: el panel de escritorio lleva su opacidad inline y
	// una clase nunca le ganaría, y el drawer de vaul maneja su propio
	// `transform`. Los literales van completos porque Tailwind escanea texto.
	const COVERED_TRANSITION = 'transition-[opacity,scale] duration-200 ease-out';
	const coveredPanel = !hidden
		? COVERED_TRANSITION
		: isMobile
			? `${COVERED_TRANSITION} opacity-0 scale-95 delay-150 pointer-events-none`
			: `${COVERED_TRANSITION} opacity-0 scale-95 delay-0 pointer-events-none`;

	// Cierre háptico centralizado para el modal de escritorio (backdrop + X). En
	// mobile el cierre lo dispara el primitivo `Drawer`, así que no se duplica.
	const handleClose = () => {
		playHaptic('tap');
		onClose?.();
	};

	const handleWheelUnderScrollLock = wheelScrollUnderOverlayLock;

	// New content starts at the top (a step change, a new screen). Not while the user
	// types in a field of this modal: each keystroke re-renders `children`, and the
	// reset hid the field being typed in.
	useEffect(() => {
		const container = containerRef.current;
		const focused = document.activeElement;
		if (container?.contains(focused) && isTextField(focused)) return;
		container?.scrollTo(0, 0);
	}, [children]);

	// Dentro de la app: reporta que hay un modal a la vista (y de qué tipo). La
	// app decide qué hace con su menú y su franja (regla máxima en
	// `nativeBridge.ts`); en el navegador no hace nada.
	const modalKind = isMobile && fullScreenOnMobile ? 'fullscreen' : 'sheet';
	useEffect(() => {
		if (!open || hidden) return;
		return registerModal(modalKind);
	}, [open, hidden, modalKind]);

	useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
			// Apertura háptica: cubre mobile (drawer) y escritorio (portal).
			playHaptic('tap');
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [open]);

	if (!open) return null;

	if (type === 'dialog') {
		return (
			<dialog
				open={open}
				className={cn('fixed z-[100]', coveredPanel, sx)}
				onClose={handleClose}
			>
				<div>{children}</div>
			</dialog>
		);
	}

	if (isMobile && fullScreenOnMobile) {
		return createPortal(
			<div className={cn('fixed inset-0 z-[100] bg-white', coveredPanel)}>
				<div
					ref={containerRef}
					className={cn(
						'h-full flex flex-col overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
						stripDrawerConflictingClasses(sx),
					)}
				>
					{children}
				</div>
				{showCloseButton && (
					<div
						onClick={handleClose}
						style={{ boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
						className='absolute top-3 right-3 cursor-pointer text-black bg-white rounded-full p-2 z-30'
					>
						<X size={18} weight='bold' />
					</div>
				)}
			</div>,
			document.body,
		);
	}

	if (isMobile && !floatOnMobile) {
		// A modal that declares a height in its `sx` (PDF viewer, declaration
		// detail, …) wants to fill the sheet, so the drawer takes a fixed,
		// viewport-relative height and its content stretches to fill it.
		// Modals with no height (confirmations, alerts) hug their content.
		const wantsFixedHeight =
			/(?:^|\s)(?:(?:sm|md|lg|xl|2xl):)?!?(?:min-|max-)?h-\S/.test(sx);
		return (
			<Drawer
				open={open}
				onOpenChange={(next) => {
					if (!next) onClose?.();
				}}
			>
				<DrawerContent
					hideCloseButton={!showCloseButton}
					hideHandle={flush}
					// A nested searchable select (BottomSheetSelect) renders its sheet
					// in a portal on document.body, outside this content. Vaul would read
					// taps on it as "outside" and dismiss the drawer. Skip the dismiss
					// when the interaction happens inside that portal.
					onInteractOutside={(e) => {
						if (
							(e.target as HTMLElement)?.closest('[data-bottomsheet-portal]')
						) {
							e.preventDefault();
						}
					}}
					className={cn(
						'bg-white',
						wantsFixedHeight ? 'h-[85dvh]' : 'max-h-[90dvh]',
						flush && 'overflow-hidden',
						coveredPanel,
					)}
				>
					<DrawerTitle className='sr-only'>Información</DrawerTitle>
					<div
						ref={containerRef}
						className={cn(
							'min-h-0 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
							wantsFixedHeight && 'flex-1',
							stripDrawerConflictingClasses(sx),
						)}
					>
						{children}
					</div>
				</DrawerContent>
			</Drawer>
		);
	}

	return createPortal(
		<div className='fixed inset-0 z-[100]'>
			{/*
			  * Cada modal pinta SU propio gris, y por eso uno anidado se
			  * distingue del que tiene debajo. Se intentó un scrim único para
			  * toda la app y el precio fue justo ese: con una sola capa, el
			  * segundo modal se apoyaba sobre el blanco del primero y parecían
			  * uno solo partido a la mitad (visto el 9-sep).
			  */}
			<motion.div
				className={cn(
					'fixed inset-0 bg-black/30',
					hidden && 'pointer-events-none',
				)}
				onClick={handleClose}
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.15 }}
			/>
			<div
				className={cn(
					'fixed inset-0 flex items-center justify-center p-4 pointer-events-none',
					floatOnMobile && 'max-md:pt-[max(1rem,env(safe-area-inset-top))] max-md:pb-[max(1rem,env(safe-area-inset-bottom))]',
					coveredPanel,
				)}>
				<motion.div
					ref={containerRef}
					onClick={(e) => e.stopPropagation()}
					onWheel={handleWheelUnderScrollLock}
					className={`relative bg-white w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain p-4 lg:p-5 rounded-3xl pointer-events-auto ${sx}`}
					initial={{ opacity: 0, scale: 0.96 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
				>
					{children}
					{showCloseButton && (
						<div
							onClick={handleClose}
							style={{ boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px' }}
							className='absolute top-3 right-3 cursor-pointer text-black bg-white rounded-full p-2 z-30'
						>
							<X size={18} weight='bold' />
						</div>
					)}
				</motion.div>
			</div>
		</div>,
		document.body,
	);
};

export default Modal;
